// src/stores/utils/storeUtils.ts

/**
 * Safely get nested property value
 */
export const safeGet = (obj: any, path: string, defaultValue: any = undefined) => {
    const keys = path.split('.');
    let result = obj;

    for (const key of keys) {
        if (result == null || typeof result !== 'object') {
            return defaultValue;
        }
        result = result[key];
    }

    return result !== undefined ? result : defaultValue;
};

/**
 * Safely set nested property value
 */
export const safeSet = (obj: any, path: string, value: any) => {
    const keys = path.split('.');
    const lastKey = keys.pop();

    if (!lastKey) return;

    let current = obj;
    for (const key of keys) {
        if (!(key in current) || typeof current[key] !== 'object') {
            current[key] = {};
        }
        current = current[key];
    }

    current[lastKey] = value;
};

/**
 * Initialize default filters for content store
 */
export const getDefaultFilters = () => ({
    searchTerm: '',
    contentKind: 'all',
    status: 'all',
    sortField: 'registrationDate',
    sortOrder: 'desc' as 'asc' | 'desc'
});

/**
 * Ensure filters object has all required properties
 */
export const ensureFilters = (filters: any) => {
    const defaultFilters = getDefaultFilters();

    if (!filters || typeof filters !== 'object') {
        return defaultFilters;
    }

    return {
        searchTerm: filters.searchTerm || defaultFilters.searchTerm,
        contentKind: filters.contentKind || defaultFilters.contentKind,
        status: filters.status || defaultFilters.status,
        sortField: filters.sortField || defaultFilters.sortField,
        sortOrder: filters.sortOrder || defaultFilters.sortOrder
    };
};

/**
 * Check if a value is empty (null, undefined, empty string, empty array)
 */
export const isEmpty = (value: any): boolean => {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Generate mock ID
 */
export const generateMockId = (): string => {
    return `mock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Deep clone object
 */
export const deepClone = <T>(obj: T): T => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
    if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
    if (typeof obj === 'object') {
        const cloned = {} as T;
        Object.keys(obj as any).forEach(key => {
            (cloned as any)[key] = deepClone((obj as any)[key]);
        });
        return cloned;
    }
    return obj;
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

/**
 * Get error message from various error types
 */
export const getErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.data?.error?.message) return error.response.data.error.message;
    return 'Có lỗi xảy ra';
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Generate random color
 */
export const generateRandomColor = (): string => {
    const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
        '#FFEAA7', '#DDA0DD', '#F0A500', '#FF7675'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
};

/**
 * Calculate protection score from settings
 */
export const calculateProtectionScore = (protection: any): number => {
    if (!protection || typeof protection !== 'object') return 50;

    let score = 50; // Base score

    if (protection.watermark) score += 15;
    if (protection.aiMonitoring) score += 20;
    if (protection.blockchain) score += 15;
    if (protection.fingerprintEnabled) score += 10;
    if (protection.drmEnabled) score += 10;

    return Math.min(score, 100); // Cap at 100
};